import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

// Two-tier hybrid model with a shared +$5/mo logistics fee.
export type Tier = "vip" | "club" | "pro" | "curated" | "signature";

export const TIER_LABELS: Record<Tier, string> = {
  vip: "VIP Autopilot",
  club: "Monthly Floral Club",
  pro: "FLWR Club Pro",
  curated: "The Curated Plan",
  signature: "The Signature Floral Plan",
};

export const TIER_PRICES: Record<
  Tier,
  { priceId: string; base: number; baseLabel: string; totalLabel: string; feeLabel: string }
> = {
  vip: {
    priceId: "vip_autopilot_monthly",
    base: 999,
    baseLabel: "$9.99",
    totalLabel: "$9.99",
    feeLabel: "$0.00",
  },
  club: {
    priceId: "monthly_floral_club_monthly",
    base: 12900,
    baseLabel: "$129.00",
    totalLabel: "$129.00",
    feeLabel: "$0.00",
  },
  pro: {
    priceId: "pro_monthly",
    base: 4900,
    baseLabel: "$49.00",
    totalLabel: "$49.00",
    feeLabel: "$0.00",
  },
  curated: {
    priceId: "flwr_club_monthly_curated",
    base: 4999,
    baseLabel: "$49.99",
    totalLabel: "$54.99",
    feeLabel: "$5.00",
  },
  signature: {
    priceId: "flwr_club_monthly_flowers",
    base: 12500,
    baseLabel: "$125.00",
    totalLabel: "$130.00",
    feeLabel: "$5.00",
  },
};

// Kept for back-compat with any lingering imports.
export const CLUB_PRICE_ID = TIER_PRICES.curated.priceId;
export const CLUB_PRICE_AMOUNT = TIER_PRICES.curated.baseLabel;

export const getMySubscription = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { environment: "sandbox" | "live" }) => data)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: sub, error } = await supabase
      .from("subscriptions")
      .select(
        "id, tier, status, current_period_end, cancel_at_period_end, committed_until, default_recipient_id, price_id",
      )
      .eq("user_id", userId)
      .eq("environment", data.environment)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return sub;
  });

export const setDefaultRecipient = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { subscriptionId: string; recipientId: string | null }) => data)
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("subscriptions")
      .update({ default_recipient_id: data.recipientId })
      .eq("id", data.subscriptionId)
      .eq("user_id", userId);
    if (error) throw error;
    return { ok: true };
  });
